import { OPEN_CCD_EVENT, CLOSE_CCD_TOUR_EVENT } from '@adobe-fonts/site/events/CCDEvent.js';
import { EXPANDED_EVENT, COLLAPSED_EVENT } from '@adobe-fonts/site/events/FlyoutEvent.js';
import { EVENT_NAME as SEARCH_PARAM_CHANGED_EVENT_NAME } from '@adobe-fonts/site/events/SearchParamChangedEvent.js';

import { openCreativeCloudDesktop } from '../components/dialogs/FontActivationFirstMileDialog.ts';

import { getAutocompleteResults } from '../util/family_autocomplete_search_util.js';
import { trapFocus, releaseFocus } from '../util/focus.js';
import { addPageAction, setCustomAttribute, getReferrerHeader } from '../util/new_relic.js';
import { getData } from '../util/script_data.js';
import { getUserPrefs, updatePrefs } from '../util/user_prefs_util.js';
import UrlService from '../util/url_service.js';
import {
  getActivityState,
  getAdobeProfileId,
  getDaysSinceSyncActivity,
  getPlan,
  setActivityState,
  setAdobeProfileId,
  setDaysSinceSyncActivity,
  setPlan
} from '../util/user_state.js';
const cookie = require('../util/cookie.js');

class SitewideHeaderController {
  constructor() {
    this.element = document.querySelector('#sitewide-header');
    this.topAppBarTarget = document.querySelector('#top-app-bar-content-target');

    this.subNav = this.element.querySelector('#header-subnav');
    this.topNav = this.element.querySelector('#header-top-nav');
  }

  init() {
    this._preloadData();
    this._setNewrelicCustomAttributes();
    this.initEventHandlers(this.element);

    window.addEventListener('DOMContentLoaded', () => {
      this.initUserPrefsEventHandlers(this.element);
    });

    // TODO(ephraim): remove this once UniversalNav is launched since we won't be
    // handling sign in this way anymore.
    const signIn = document.querySelector('.adobe-fonts-header__sign-in');
    if (!signIn) {
      return;
    }

    signIn.addEventListener('click', this._handleSignInButton);
  }

  _preloadData() {
    const userInfo = getData('/user_info');
    if (!userInfo) {
      return;
    }

    setActivityState(userInfo.activity_state);
    setAdobeProfileId(userInfo.adobe_profile_id);
    setDaysSinceSyncActivity(userInfo.days_since_sync_activity);
    setPlan(userInfo.plan);
  }

  /**
 * Handles a open CCD app button on the manage fonts menu
 */
  _handleOpenCCDButton = () => {
    addPageAction('typekit.click.use-model-font-activation-open-app', { source: 'Manage Fonts' });
    openCreativeCloudDesktop();
  };

  _setNewrelicCustomAttributes = () => {
    const referrer = UrlService.getSearchParam('referrer');
    if (referrer) {
      setCustomAttribute('referrer', referrer);
    }

    setCustomAttribute('activityState', getActivityState());
    setCustomAttribute('adobeProfileId', getAdobeProfileId());
    setCustomAttribute('daysSinceSyncActivity', getDaysSinceSyncActivity());
    setCustomAttribute('plan', getPlan());
    setCustomAttribute('referrerHeader', getReferrerHeader());
    if (cookie('require_login_experiment_state')) {
      setCustomAttribute('requireLoginExperimentNumRejections', cookie('require_login_experiment_num_rejections'));
      setCustomAttribute('requireLoginExperimentShowDialog', cookie('require_login_experiment_show_dialog'));
      setCustomAttribute('requireLoginExperimentState', cookie('require_login_experiment_state'));
    }
  };

  initEventHandlers = (headerEl) => {
    // We are transitioning from subnav to topnav, using a flipper flag.
    // If the subnav exists, use that. If not, that means the topnav exists, so use that.
    const nav = this.subNav || this.topNav;

    if (nav) {
      nav.addEventListener(SEARCH_PARAM_CHANGED_EVENT_NAME, this.getFamilyAutocompleteSearchResults);
    }

    headerEl.addEventListener(EXPANDED_EVENT, () => {
      trapFocus();
      this.element.inert = false;
    });

    headerEl.addEventListener(COLLAPSED_EVENT, releaseFocus);

    headerEl.addEventListener(OPEN_CCD_EVENT, this._handleOpenCCDButton);
  };

  initUserPrefsEventHandlers = async (headerEl) => {
    try {
      headerEl.addEventListener(CLOSE_CCD_TOUR_EVENT, () => {
        updatePrefs({ hideUseModelCCDTutorial: true });
      });
      const userPrefs = await getUserPrefs();
      const shouldShowCoachmark = !userPrefs.hideUseModelCCDTutorial;

      const nav = this.subNav || this.topNav;

      if (nav) {
        nav.setAttribute('showCoachmark', shouldShowCoachmark);
      }
    } catch (error) {
      if (this._isProdEnvironment()) {
        return;
      }
      console.error('Failed to initialize user prefs:', error);
    }
  };

  _handleSignInButton = () => {
    addPageAction('typekit.click.sign-in');
  };

  getFamilyAutocompleteSearchResults = async (event) => {
    const searchbar = event.target;

    try {
      const response = await getAutocompleteResults(event.detail.searchParam);
      searchbar.results = response.data;
    } catch (error) {
      if (this._isProdEnvironment()) {
        return;
      }
      console.error('Failed to get family autocomplete search results:', error);
    }
  };

  _isProdEnvironment = () => {
    return this.element.getAttribute('env') === 'prod';
  };
}

export default SitewideHeaderController;
